import { useState, useRef, useEffect, FormEvent } from "react";
import { useTranslation } from "react-i18next";

const FORM_ENDPOINT = "/contact-processor.php";

interface ContactFormProps {
  onClose?: () => void;
  isOpen?: boolean;
}

export function ContactForm({ onClose, isOpen }: ContactFormProps) {
  const { t } = useTranslation('common');

  // Helper to ensure string type for placeholders
  const ts = (key: string): string => t(key) as string;
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Focus first input when form opens
  useEffect(() => {
    if (isOpen && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [isOpen]);

  // Reset form when closed
  useEffect(() => {
    if (!isOpen) {
      setSubmitted(false);
      setIsSubmitting(false);
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check - if filled, it's a bot
    if (formData.get("website")) {
      setSubmitted(true);
      setIsSubmitting(false);
      return;
    }

    // Validate fields
    const name = formData.get("name")?.toString().trim();
    const email = formData.get("email")?.toString().trim();
    const message = formData.get("message")?.toString().trim();

    if (!name || !email || !message) {
      setError(t('contact.required'));
      setIsSubmitting(false);
      return;
    }

    if (message.length < 10) {
      setError("Please enter a longer message.");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch {
      setError("Failed to send. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && onClose) {
      onClose();
    }
  };

  if (submitted) {
    return (
      <div className="contact-success" role="alert">
        <div className="contact-success-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h3 className="contact-success-title">{t('contact.success')}</h3>
        <p className="contact-success-message">{t('contact.successMessage')}</p>
        {onClose && (
          <button
            type="button"
            className="contact-btn contact-btn-secondary"
            onClick={onClose}
          >
            {t('contact.close')}
          </button>
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      method="POST"
      className="contact-form"
      onKeyDown={handleKeyDown}
    >
      <h2 className="contact-title">{t('contact.title')}</h2>

      {error && (
        <div className="contact-error" role="alert">
          {error}
        </div>
      )}

      {/* Honeypot field - hidden from users, bots will fill it */}
      <input
        type="text"
        name="website"
        className="sr-only"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <div className="contact-field">
        <label htmlFor="contact-name" className="contact-label">
          {t('contact.name')}
          <span className="contact-required" aria-hidden="true">*</span>
        </label>
        <input
          ref={firstInputRef}
          type="text"
          id="contact-name"
          name="name"
          placeholder={ts('contact.namePlaceholder')}
          required
          aria-required="true"
          className="contact-input"
          autoComplete="name"
        />
      </div>

      <div className="contact-field">
        <label htmlFor="contact-email" className="contact-label">
          {t('contact.email')}
          <span className="contact-required" aria-hidden="true">*</span>
        </label>
        <input
          type="email"
          id="contact-email"
          name="email"
          placeholder={ts('contact.emailPlaceholder')}
          required
          aria-required="true"
          className="contact-input"
          autoComplete="email"
        />
      </div>

      <div className="contact-field contact-field-message">
        <label htmlFor="contact-message" className="contact-label">
          {t('contact.message')}
          <span className="contact-required" aria-hidden="true">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          placeholder={ts('contact.messagePlaceholder')}
          required
          aria-required="true"
          className="contact-textarea"
          rows={5}
        />
      </div>

      <button
        type="submit"
        className="contact-btn contact-btn-primary"
        disabled={isSubmitting}
      >
        {isSubmitting ? t('contact.sending') : t('contact.submit')}
      </button>
    </form>
  );
}
